import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, X } from 'lucide-react';

const CONSENT_KEY = 'daniels_cookie_consent';

function getConsent() {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function saveConsent(consent) {
  localStorage.setItem(CONSENT_KEY, JSON.stringify({
    ...consent,
    timestamp: new Date().toISOString(),
  }));
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [categories, setCategories] = useState({
    necessary: true, // immer aktiv
    statistics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = getConsent();
    if (!consent) {
      // Verzögerung damit die Seite erst lädt
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptAll = () => {
    const consent = { necessary: true, statistics: true, marketing: true };
    setCategories(consent);
    saveConsent(consent);
    setVisible(false);
  };

  const acceptNecessary = () => {
    const consent = { necessary: true, statistics: false, marketing: false };
    setCategories(consent);
    saveConsent(consent);
    setVisible(false);
  };

  const saveSelection = () => {
    saveConsent(categories);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="fixed bottom-0 left-0 right-0 z-[70] p-4 sm:p-6"
        data-testid="cookie-consent-banner"
      >
        <div className="max-w-4xl mx-auto bg-white border border-gray-200 shadow-2xl p-6 sm:p-8">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6 text-[#2D5A27] flex-shrink-0" strokeWidth={1.5} />
              <h3 className="font-['Outfit'] text-lg font-bold text-[#1A3A22]">
                Datenschutz-Einstellungen
              </h3>
            </div>
            <button
              onClick={acceptNecessary}
              className="text-gray-400 hover:text-gray-600"
              data-testid="cookie-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <p className="font-['Manrope'] text-gray-600 text-sm leading-relaxed mb-4">
            Wir verwenden auf dieser Website keine Tracking-Cookies und keine Analyse-Tools.
            Es werden ausschließlich technisch notwendige Funktionen genutzt. Ihre Daten werden
            nicht an Dritte weitergegeben.
          </p>

          {showDetails && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              className="mb-4 space-y-3"
            >
              {/* Notwendig */}
              <div className="flex items-center justify-between p-3 bg-[#F4F8F4] border border-gray-100">
                <div>
                  <span className="font-['Manrope'] text-sm font-semibold text-[#1A3A22]">Technisch notwendig</span>
                  <p className="font-['Manrope'] text-xs text-gray-500 mt-0.5">
                    Consent-Speicherung. Keine personenbezogenen Daten.
                  </p>
                </div>
                <span className="font-['Manrope'] text-xs text-[#2D5A27] font-medium bg-[#2D5A27]/10 px-2 py-1">
                  Immer aktiv
                </span>
              </div>

              {/* Statistik */}
              <div className="flex items-center justify-between p-3 border border-gray-100">
                <div>
                  <span className="font-['Manrope'] text-sm font-semibold text-[#1A3A22]">Statistik</span>
                  <p className="font-['Manrope'] text-xs text-gray-500 mt-0.5">
                    Derzeit nicht aktiv. Reserviert für zukünftige anonyme Auswertungen.
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer" data-testid="cookie-statistics-toggle">
                  <input
                    type="checkbox"
                    checked={categories.statistics}
                    onChange={(e) => setCategories({ ...categories, statistics: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-200 peer-checked:bg-[#2D5A27] rounded-full transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full"></div>
                </label>
              </div>

              {/* Marketing */}
              <div className="flex items-center justify-between p-3 border border-gray-100">
                <div>
                  <span className="font-['Manrope'] text-sm font-semibold text-[#1A3A22]">Marketing</span>
                  <p className="font-['Manrope'] text-xs text-gray-500 mt-0.5">
                    Derzeit nicht aktiv. Reserviert für zukünftige Werbemaßnahmen.
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer" data-testid="cookie-marketing-toggle">
                  <input
                    type="checkbox"
                    checked={categories.marketing}
                    onChange={(e) => setCategories({ ...categories, marketing: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-200 peer-checked:bg-[#2D5A27] rounded-full transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full"></div>
                </label>
              </div>
            </motion.div>
          )}

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <button
              onClick={acceptNecessary}
              data-testid="cookie-accept-necessary"
              className="px-6 py-2.5 border border-gray-200 hover:border-[#2D5A27] font-['Manrope'] text-sm font-medium text-[#1A3A22] transition-colors"
            >
              Nur notwendige
            </button>
            <button
              onClick={() => setShowDetails(!showDetails)}
              data-testid="cookie-show-details"
              className="px-6 py-2.5 border border-gray-200 hover:border-[#2D5A27] font-['Manrope'] text-sm font-medium text-[#1A3A22] transition-colors"
            >
              {showDetails ? 'Weniger' : 'Einstellungen'}
            </button>
            {showDetails && (
              <button
                onClick={saveSelection}
                data-testid="cookie-save-selection"
                className="px-6 py-2.5 bg-[#2D5A27] hover:bg-[#10B981] text-white font-['Manrope'] text-sm font-medium transition-colors"
              >
                Auswahl speichern
              </button>
            )}
            <button
              onClick={acceptAll}
              data-testid="cookie-accept-all"
              className="px-6 py-2.5 bg-[#2D5A27] hover:bg-[#10B981] text-white font-['Manrope'] text-sm font-medium transition-colors sm:ml-auto"
            >
              Alle akzeptieren
            </button>
          </div>

          <p className="font-['Manrope'] text-xs text-gray-400 mt-4">
            Weitere Informationen finden Sie in unserer Datenschutzerklärung. Sie können Ihre Einstellungen
            jederzeit über den Link im Footer ändern.
          </p>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

// Export helper to check consent status
export function hasConsent(category) {
  const consent = getConsent();
  if (!consent) return false;
  return consent[category] === true;
}

// Export helper to reset consent (for Footer link)
export function resetConsent() {
  localStorage.removeItem(CONSENT_KEY);
  window.location.reload();
}
